import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import type { LocaleLanguages } from "./utils";
import { getLocaleByCode } from "./utils";
import i18n from "../src/i18n";
import { AppLayout } from "./AppLayout";
import { SnackbarProvider } from "./shared/components/snackbar/Snackbar";
import type { UppState } from "./App";
import getEnvironment from "./config/environment";
import { useEffect } from "react";
import ErrorBoundary from "./layout/ErrorBoundary";
import {
  getCustomerIdFromToken,
  getUserIdFromToken,
} from "./utils/encrypt-decrypt-utilities";
import { useDispatch, useSelector } from "react-redux";
import {
  updateCurrentCustomerId,
  updateCurrentUserId,
} from "./store/NotificationsSlice";
import { userSignedIn } from "./store/UserSlice";
import type { RootState } from "./store/store";
import { installAuthSessionCleanup } from "./utils/auth-session/auth-session";
import { isTeamAccessLost } from "./utils/active-team/active-team";
import { getAccessTokenFromStorage } from "./utils/auth-session/access-token";

const envName = getEnvironment();

if (envName.name == "development") {
  require("./__mocks__");
}

interface AppWrapperProps {
  isUpp?: boolean;
  uppState?: UppState;
  pushHistoryAction?: (_path: string) => void;
  clearKycRedirectAction: () => void;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
});

export const AppWrapper: React.FC<AppWrapperProps> = ({
  isUpp,
  uppState,
  pushHistoryAction,
  clearKycRedirectAction
}: AppWrapperProps) => {
  const dispatch = useDispatch();
  const isSignedIn = useSelector((state: RootState) => state.user.isSignedIn);

  useEffect(() => {
    installAuthSessionCleanup(isUpp);
  }, [isUpp]);

  useEffect(() => {
    const token = getAccessTokenFromStorage();
    if (token && !isSignedIn && !isUpp) {
      dispatch(userSignedIn(token));
    }
  }, [dispatch, isSignedIn, isUpp]);

  useEffect(() => {
    const handleError = (e: ErrorEvent): void => {
      console.warn("Error while loading the App in app wrapper", e);
    };

    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener("error", handleError);
    };
  }, []);

  useEffect(() => {
    const storedAccessToken = getAccessTokenFromStorage();
    if (!storedAccessToken) return;

    const userId = getUserIdFromToken(storedAccessToken);
    const customerId = getCustomerIdFromToken(storedAccessToken);

    if (!userId || !customerId) return;

    dispatch(updateCurrentCustomerId(Number(customerId)));
    dispatch(updateCurrentUserId(Number(userId)));
  }, [dispatch, isSignedIn]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={getLocaleByCode(i18n.language as LocaleLanguages)}
          >
            <SnackbarProvider shouldSuppressErrorSnackbar={isTeamAccessLost}>
              <AppLayout
                isUpp={isUpp}
                uppState={uppState}
                pushHistoryAction={pushHistoryAction}
                clearKycRedirectAction={clearKycRedirectAction}
              />
            </SnackbarProvider>
          </LocalizationProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};
